import { findFirstNode } from '../../../components/Tree/helpers/helpers';
import { unique } from '../../../utils/array/array';
import { VALID_PUBLISHED_STATES } from '../../../utils/category/defines';
import { selectSearchCatalogHits, selectTreeDataByTreeId } from './tree';
import {
  STATE_INVALID_CATEGORY_IMAGE,
  STATE_NEW,
  STATE_VERIFIED,
} from '../../../utils/category/states';
import { createShallowEqualResultSelector, getCatalogIdFromTreeId } from '../utils';
import {
  selectInvalidImages,
  selectUnpublishedCategories,
  selectUntranslatedCategories,
} from '../../slices/indicator';
import type { Catalog } from '../../../entities/Catalog';
import type { NodeBase, NodeBaseItem } from '../../../entities/Node';
import { selectCatalogs } from '../../slices/catalog';

interface CategoryStates {
  [key: string]: number;
}

export const selectSearchCatalogList = createShallowEqualResultSelector(
  selectSearchCatalogHits,
  selectCatalogs,
  (hits: string[], catalogs: Catalog[]) =>
    hits
      .map((catalogId) =>
        catalogs.find((catalog) => catalog.catalogId === catalogId),
      )
      .filter((c): c is Catalog => !!c),
);

export const selectSearchAvailableFlags = createShallowEqualResultSelector(
  selectTreeDataByTreeId,
  selectUnpublishedCategories,
  selectUntranslatedCategories,
  selectInvalidImages,
  getCatalogIdFromTreeId,
  (
    treeData: NodeBase[],
    unpublishedCategories: { catalogId: string; categoryId: string }[],
    untranslatedCategories: { catalogId: string; categoryId: string }[],
    invalidImages: { catalogId: string; categoryId: string }[],
    catalogId: string,
  ): CategoryStates => {
    const flags = unique(
      [...unpublishedCategories, ...untranslatedCategories, ...invalidImages]
        .filter((c) => c.catalogId === catalogId)
        .map((c) => c.categoryId),
    ).reduce((categoryStates: CategoryStates, categoryId: string) => {
      const foundNode = findFirstNode({
        treeData,
        searchMethod: (ni: NodeBaseItem) => ni.node?.categoryId === categoryId,
        ignoreCollapsed: false,
      }) as unknown as { node: NodeBase } | null;

      if (!foundNode) return categoryStates;

      const {
        node: { category = null, invalidCategoryImage = null },
      } = foundNode;
      if (!category) {
        return categoryStates;
      }

      const hasChildren =
        !!category?.childCategoryCount || !!category.childItemCount;

      if (invalidCategoryImage && hasChildren) {
        categoryStates[STATE_INVALID_CATEGORY_IMAGE] =
          (categoryStates[STATE_INVALID_CATEGORY_IMAGE] || 0) + 1;
      }

      if (category.newCategory) {
        categoryStates[STATE_NEW] = (categoryStates[STATE_NEW] || 0) + 1;
      }

      if (
        category.publishState &&
        !VALID_PUBLISHED_STATES.includes(category.publishState)
      ) {
        categoryStates[category.publishState] =
          (categoryStates[category.publishState] || 0) + 1;
      }
      if (category.translateState && category.translateState !== STATE_VERIFIED) {
        categoryStates[category.translateState] =
          (categoryStates[category.translateState] || 0) + 1;
      }

      return categoryStates;
    }, {});
    return flags;
  },
);
